/*
 * Purpose: Gemini-style home hero — thin wrapper around SuperIntentInterpreter
 * Last modified: 2026-06-28 | Agent
 * Completeness: 96
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, FolderOpen, LayoutDashboard, LayoutGrid, Wand2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RotatingOutcomeHeadline } from '@/components/home/RotatingOutcomeHeadline';
import { SuperIntentInterpreter } from '@/components/intent/SuperIntentInterpreter';
import { AVERAGE_OUTCOME_SEEDS } from '@/seeds/averageOutcomes';
import { useAutoBlocksStore } from '@/stores/autoBlocksStore';
import { findBuilderNavItem } from '@/lib/productActivities';
import { REMEDIATION } from '@/lib/remediationRoutes';
import { PRODUCT_TERMS } from '@/lib/productTerms';
import { motionClasses } from '@/motion';
import { HomeSeoSection } from '@/components/home/HomeSeoSection';
import { HOME_FAQ } from '@/lib/seoMeta';

const STUCK_CHIPS = AVERAGE_OUTCOME_SEEDS.slice(0, 5);
const MY_FLOWS = findBuilderNavItem('flows');

export function HomeOutcomeHero() {
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');

  const goBuild = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      // Seed the mission, then land on the Mission map and watch the AI build it live.
      useAutoBlocksStore.getState().startMission(trimmed, { autopilot: true });
      navigate(`${REMEDIATION.mission}?building=1`);
    },
    [navigate]
  );

  return (
    <div className="min-h-[calc(100dvh-3.5rem)] flex flex-col relative overflow-x-hidden">
      <div className="relative flex-1 flex flex-col items-center justify-center px-4 sm:px-6 py-10 sm:py-16 max-w-3xl mx-auto w-full">
        <div className="ab-home-hero-glow pointer-events-none" aria-hidden />

        <div className="relative z-10 w-full flex flex-col items-center">
          <div className="mb-8 sm:mb-10 w-full relative z-10">
            <RotatingOutcomeHeadline />
            <p className="text-center text-sm text-muted-foreground mt-4 max-w-md mx-auto relative z-10">
              Describe your outcome — watch Autoblocks build it live on the map. Take control anytime, then connect and run.
            </p>
          </div>

          <SuperIntentInterpreter
            variant="home"
            value={draft}
            onChange={setDraft}
            onSubmit={() => goBuild(draft)}
            className="z-10"
          />

          <div className="w-full max-w-2xl mt-10 sm:mt-12 relative z-10">
            <p className="text-xs font-medium text-muted-foreground mb-3 text-center sm:text-left">
              If you&apos;re stuck, try one of these
            </p>
            <div className="flex flex-wrap gap-2 justify-center sm:justify-start">
              {STUCK_CHIPS.map((seed) => (
                <button
                  key={seed.id}
                  type="button"
                  onClick={() => goBuild(seed.outcomeText)}
                  className={`rounded-full border border-border/70 bg-card/60 backdrop-blur-sm px-3 py-2 text-xs sm:text-sm text-foreground/90 hover:bg-card hover:border-border transition-colors ui-lift-subtle ${motionClasses.chip} text-left max-w-full`}
                >
                  {seed.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <HomeSeoSection />

      <section className="relative z-10 w-full max-w-3xl mx-auto px-4 sm:px-6 pb-8 space-y-4" aria-labelledby="home-faq-heading">
        <h2 id="home-faq-heading" className="text-base font-semibold text-foreground text-center sm:text-left">
          Frequently asked — vs Zapier, Make &amp; n8n
        </h2>
        <div className="space-y-3">
          {HOME_FAQ.map((item) => (
            <details key={item.question} className="rounded-lg border border-border/60 bg-card/30 px-4 py-3 group">
              <summary className="text-sm font-medium text-foreground cursor-pointer list-none flex justify-between gap-2">
                {item.question}
              </summary>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{item.answer}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="relative shrink-0 border-t border-border/40 px-4 py-3 flex flex-wrap items-center justify-center gap-3 text-xs text-muted-foreground">
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate(REMEDIATION.dashboard)}>
          <LayoutDashboard className="h-3.5 w-3.5 mr-1" />
          Dashboard
        </Button>
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate(REMEDIATION.flows)}>
          <FolderOpen className="h-3.5 w-3.5 mr-1" />
          {MY_FLOWS?.label ?? 'My flows'}
        </Button>
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate(REMEDIATION.connect)}>
          <Wand2 className="h-3.5 w-3.5 mr-1" />
          Connect
        </Button>
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate(REMEDIATION.mission)}>
          <LayoutGrid className="h-3.5 w-3.5 mr-1" />
          {PRODUCT_TERMS.mission}
        </Button>
        <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => navigate('/help')}>
          <BookOpen className="h-3.5 w-3.5 mr-1" />
          Help
        </Button>
      </footer>
    </div>
  );
}
